import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import Checkbox from "../../components/common/Checkbox";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import PageHeader from "../../components/common/PageHeader";
import Toast from "../../components/common/Toast";
import { useStationFeature } from "../../hooks/useStationFeature";
import { SHOW_TIP_UI } from "../../utils/uiFlags";
import {
  useDeleteKitchenDisplayBackgroundMutation,
  useDeleteOrderDisplayBackgroundMutation,
  useGetLocalDevicesQuery,
  useGetKiosksQuery,
  useUploadAttractBannerImageMutation,
  useUpdateFulfillmentSettingsMutation,
  useUpdateStoreFeaturesMutation,
  useUploadKitchenDisplayBackgroundMutation,
  useUploadOrderDisplayBackgroundMutation,
} from "../../store/api";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";
import {
  normalizeBlockedOrderChannels,
} from "../../utils/orderBlocking";
import { hasAnyScreenAccess } from "../../utils/permissions";

const AUTO_ARCHIVE_MIN = 10;
const AUTO_ARCHIVE_MAX = 3600;
const KDS_MAX_VISIBLE_ORDERS_MIN = 1;
const KDS_MAX_VISIBLE_ORDERS_MAX = 100;
const TIP_PRESET_LIMIT = 3;
const MAX_BACKGROUND_BYTES = 5 * 1024 * 1024;
const MAX_BANNER_BYTES = 5 * 1024 * 1024;
const MAX_BANNER_DIMENSION = 4096;

const getApiErrorCode = (err) =>
  err?.data?.code || err?.data?.errorCode || err?.data?.title || null;

const getApiErrorMessage = (err, fallback) => {
  if (typeof err?.data === "string") {
    return err.data;
  }
  return (
    err?.data?.detail ||
    err?.data?.message ||
    err?.data?.title ||
    fallback
  );
};

const validateBackgroundFile = (file) => {
  if (!file) {
    return "Background image file is required.";
  }
  if (!["image/png", "image/jpeg"].includes(file.type)) {
    return "Background image must be PNG or JPG.";
  }
  if (file.size > MAX_BACKGROUND_BYTES) {
    return "Background image is too large. Max size is 5MB.";
  }
  return "";
};

const parseAutoArchiveSeconds = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return { value: null, error: null };
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    return {
      value: null,
      error: "Auto archive seconds must be a whole number.",
    };
  }

  if (parsed < AUTO_ARCHIVE_MIN || parsed > AUTO_ARCHIVE_MAX) {
    return {
      value: null,
      error: `Auto archive seconds must be between ${AUTO_ARCHIVE_MIN} and ${AUTO_ARCHIVE_MAX}.`,
    };
  }

  return { value: parsed, error: null };
};

const parseKdsMaxVisibleOrders = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return { value: null, error: null };
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    return {
      value: null,
      error: "KDS max visible orders must be a whole number.",
    };
  }

  if (
    parsed < KDS_MAX_VISIBLE_ORDERS_MIN ||
    parsed > KDS_MAX_VISIBLE_ORDERS_MAX
  ) {
    return {
      value: null,
      error: `KDS max visible orders must be between ${KDS_MAX_VISIBLE_ORDERS_MIN} and ${KDS_MAX_VISIBLE_ORDERS_MAX}.`,
    };
  }

  return { value: parsed, error: null };
};

const parseTipPercentage = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return { value: null, error: null };
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    return {
      value: null,
      error: "Tip percentages must be whole numbers.",
    };
  }

  if (parsed <= 0) {
    return {
      value: null,
      error: "Tip percentages must be positive.",
    };
  }

  return { value: parsed, error: null };
};

const normalizeTipPercentagesInput = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => Number(String(value || "").trim()))
        .filter(
          (value) =>
            Number.isFinite(value) && Number.isInteger(value) && value > 0,
        ),
    ),
  ).slice(0, TIP_PRESET_LIMIT);

const createBannerDraft = () => ({
  id:
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `banner-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  imageUrl: "",
  sortOrder: 0,
  isActive: true,
  startAt: "",
  endAt: "",
  altText: "",
});

const padTwo = (value) => String(value).padStart(2, "0");

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(
    date.getDate(),
  )}T${padTwo(date.getHours())}:${padTwo(date.getMinutes())}`;
};

const ISO_WITH_TZ_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;

const toIsoWithTimezoneOrNull = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;

  if (ISO_WITH_TZ_REGEX.test(raw)) {
    return raw;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toISOString();
};

const getBannerWindowStatus = (banner, nowMs = Date.now()) => {
  const startAt = toIsoWithTimezoneOrNull(banner?.startAt);
  const endAt = toIsoWithTimezoneOrNull(banner?.endAt);
  const startMs = startAt ? Date.parse(startAt) : null;
  const endMs = endAt ? Date.parse(endAt) : null;

  if (startMs !== null && nowMs < startMs) return "Scheduled";
  if (endMs !== null && nowMs > endMs) return "Expired";
  return "Active";
};

const getBannerStatusToneClass = (status) => {
  if (status === "Scheduled") {
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300";
  }
  if (status === "Expired") {
    return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300";
  }
  return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300";
};

const normalizeAttractBanners = (source) => {
  if (!Array.isArray(source)) return [];
  return source.map((banner, index) => ({
    id: String(banner?.id || createBannerDraft().id),
    imageUrl: String(banner?.imageUrl || "").trim(),
    sortOrder: Number.isFinite(Number(banner?.sortOrder))
      ? Number(banner.sortOrder)
      : index + 1,
    isActive: banner?.isActive !== false,
    startAt: toDateTimeLocalValue(banner?.startAt || banner?.startDate || ""),
    endAt: toDateTimeLocalValue(banner?.endAt || banner?.endDate || ""),
    altText: typeof banner?.altText === "string" ? banner.altText : "",
  }));
};

const validateAttractBanners = (banners) => {
  for (let index = 0; index < banners.length; index += 1) {
    const banner = banners[index];
    if (!String(banner.imageUrl || "").trim()) {
      return `Banner ${index + 1}: image URL is required.`;
    }
    if (!Number.isInteger(Number(banner.sortOrder))) {
      return `Banner ${index + 1}: sort order must be a whole number.`;
    }
    const startAt = toIsoWithTimezoneOrNull(banner.startAt);
    const endAt = toIsoWithTimezoneOrNull(banner.endAt);
    if (banner.startAt && !startAt) {
      return `Banner ${index + 1}: start time is invalid.`;
    }
    if (banner.endAt && !endAt) {
      return `Banner ${index + 1}: end time is invalid.`;
    }
    if (startAt && endAt) {
      const start = Date.parse(startAt);
      const end = Date.parse(endAt);
      if (start > end) {
        return `Banner ${index + 1}: start date must be before or equal to end date.`;
      }
    }
  }
  return "";
};

const validateAttractBannerFile = async (file) => {
  if (!file) return "Banner image file is required.";
  if (!["image/png", "image/jpeg"].includes(file.type)) {
    return "Banner image must be PNG or JPG.";
  }
  if (file.size > MAX_BANNER_BYTES) {
    return "Banner image is too large. Max size is 5MB.";
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const dimensions = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () =>
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => reject(new Error("Invalid image"));
      image.src = objectUrl;
    });
    if (
      dimensions.width > MAX_BANNER_DIMENSION ||
      dimensions.height > MAX_BANNER_DIMENSION
    ) {
      return "Banner image dimensions must be 4096x4096 or smaller.";
    }
  } catch {
    return "Unable to read banner image dimensions.";
  } finally {
    URL.revokeObjectURL(objectUrl);
  }

  return "";
};

const getSettingsErrorMessage = (err) => {
  const code = getApiErrorCode(err);

  if (code === "DISPLAY_SETTINGS_NOT_FOUND" || err?.status === 404) {
    return "No display device settings found for this restaurant.";
  }

  return (
    err?.data?.detail ||
    err?.data?.message ||
    "Failed to load general settings."
  );
};

const FulfillmentSettings = () => {
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const {
    features,
    activeStoreId,
    isLoading: featuresLoading,
    isError: featuresError,
    refetch: refetchFeatures,
  } = useStoreFeatures(true);
  const {
    fulfillmentSettings,
    printTokensByStation,
    kitchenDisplayUseStations,
    orderDisplayUseStations,
    showReadyOnly,
    kitchenShowCustomerName,
    kitchenShowCustomerPhone,
    kitchenShowCustomerEmail,
    orderDisplayShowCustomerName,
    orderDisplayShowCustomerPhone,
    orderDisplayShowCustomerEmail,
    orderDisplayBackgroundUrl,
    kitchenDisplayBackgroundUrl,
    autoArchiveSeconds,
    kdsMaxVisibleOrders,
    posPaymentMode,
    tipConfig,
    qrBaseUrl,
    qrPrintKioskId,
    activeStations,
    isLoading,
    isError,
    error,
    refetchSettings,
    refetchStations,
  } = useStationFeature();
  const enableKiosk = Boolean(
    features?.enableKiosk ?? features?.EnableKiosk,
  );
  const { data: localDevices } = useGetLocalDevicesQuery();
  const {
    data: kiosksData,
    isLoading: kiosksLoading,
    isError: kiosksError,
    error: kiosksErrorDetail,
  } = useGetKiosksQuery(undefined, { skip: !token || !enableKiosk });
  const [updateFulfillmentSettings, updateState] =
    useUpdateFulfillmentSettingsMutation();
  const [uploadOrderDisplayBackground, uploadOrderState] =
    useUploadOrderDisplayBackgroundMutation();
  const [deleteOrderDisplayBackground, deleteOrderState] =
    useDeleteOrderDisplayBackgroundMutation();
  const [uploadKitchenDisplayBackground, uploadKitchenState] =
    useUploadKitchenDisplayBackgroundMutation();
  const [deleteKitchenDisplayBackground, deleteKitchenState] =
    useDeleteKitchenDisplayBackgroundMutation();
  const [uploadAttractBannerImage, uploadAttractBannerState] =
    useUploadAttractBannerImageMutation();
  const [updateStoreFeatures] = useUpdateStoreFeaturesMutation();
  const [form, setForm] = useState({
    printTokensByStation: false,
    kitchenDisplayUseStations: false,
    orderDisplayUseStations: false,
    showReadyOnly: false,
    kitchenShowCustomerName: false,
    kitchenShowCustomerPhone: false,
    kitchenShowCustomerEmail: false,
    orderDisplayShowCustomerName: false,
    orderDisplayShowCustomerPhone: false,
    orderDisplayShowCustomerEmail: false,
    autoArchiveSecondsInput: "",
    kdsMaxVisibleOrdersInput: "",
    posPaymentMode: "PAY_FIRST",
    tipEnabled: true,
    tipPercentage1Input: "",
    tipPercentage2Input: "",
    tipPercentage3Input: "",
    qrBaseUrl: "",
    qrPrintKioskId: "",
    attractBannersKioskId: "",
    attractBanners: [],
  });
  const [channelBlockForm, setChannelBlockForm] = useState({
    kiosk: false,
    qr: false,
    pos: false,
  });
  const [autoArchiveError, setAutoArchiveError] = useState("");
  const [kdsMaxVisibleOrdersError, setKdsMaxVisibleOrdersError] = useState("");
  const [tipSettingsError, setTipSettingsError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [bannerError, setBannerError] = useState("");
  const [bannerUploadingIndex, setBannerUploadingIndex] = useState(null);
  const [orderBackgroundError, setOrderBackgroundError] = useState("");
  const [kitchenBackgroundError, setKitchenBackgroundError] = useState("");
  const [localOrderBackgroundUrl, setLocalOrderBackgroundUrl] = useState("");
  const [localKitchenBackgroundUrl, setLocalKitchenBackgroundUrl] =
    useState("");
  const [toast, setToast] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(
    () => localStorage.getItem("edgeDeviceId") || "",
  );
  const channelBlockDirtyRef = useRef(false);
  const kitchenFileRef = useRef(null);
  const orderFileRef = useRef(null);
  const kitchenPreviewRef = useRef("");
  const orderPreviewRef = useRef("");
  const customerAutoSyncRef = useRef(false);
  const hasHydratedFormRef = useRef(false);
  const baseUrl = useMemo(
    () =>
      (process.env.REACT_APP_API_BASE_URL || "http://localhost:50268").replace(
        /\/+$/,
        "",
      ),
    [],
  );

  const getAuthHeaders = useCallback(() => {
    const deviceToken =
      localStorage.getItem("edgeDeviceToken") ||
      process.env.REACT_APP_DEVICE_TOKEN ||
      "";

    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(deviceToken ? { "X-DEVICE-TOKEN": deviceToken } : {}),
    };
  }, [token]);

  const canKds =
    Boolean(features?.enableKds) &&
    hasAnyScreenAccess(user, ["SETTINGS_FULFILLMENT"]);
  const canOrderDisplay =
    Boolean(features?.enableOrderDisplay) &&
    hasAnyScreenAccess(user, ["DEVICES_HEALTH", "DEVICES_LOCAL_DEVICES", "DEVICES_TERMINALS", "SETTINGS_FULFILLMENT"]);
  const canQr =
    Boolean(features?.enableQr) &&
      hasAnyScreenAccess(user, [
      "SETTINGS_CONFIGURE_STORE",
      "SETTINGS_FULFILLMENT",
      "SETTINGS_FAQ",
      "DEVICES_KIOSKS",
      "DEVICES_HEALTH",
      "DEVICES_LOCAL_DEVICES",
      "DEVICES_TERMINALS",
    ]);
  const showStationFeatures =
    Boolean(features?.enableKds) || Boolean(features?.enableOrderDisplay);
  const requireCustomerDetails = Boolean(
    features?.requireCustomerDetails ?? features?.RequireCustomerDetails,
  );
  const hasLocalDevices = Array.isArray(localDevices) && localDevices.length > 0;

  useEffect(() => {
    if (!hasLocalDevices) return;
    const currentDeviceId = localStorage.getItem("edgeDeviceId") || "";
    const currentDeviceExists = localDevices.some(
      (device) => device.deviceId === currentDeviceId,
    );

    if (!currentDeviceExists) {
      const fallback = localDevices[0];
      if (fallback?.deviceId) {
        setSelectedDeviceId(fallback.deviceId);
        localStorage.setItem("edgeDeviceId", fallback.deviceId);
        if (fallback.deviceKey) {
          localStorage.setItem("edgeDeviceToken", fallback.deviceKey);
        }
        if (fallback.deviceName) {
          localStorage.setItem("edgeDeviceName", fallback.deviceName);
        }
      }
      return;
    }

    setSelectedDeviceId(currentDeviceId);
  }, [hasLocalDevices, localDevices]);

  const handleDeviceChange = (nextDeviceId) => {
    const next = localDevices?.find((device) => device.deviceId === nextDeviceId);
    if (!next) return;

    setSelectedDeviceId(next.deviceId || "");
    if (next.deviceKey) {
      localStorage.setItem("edgeDeviceToken", next.deviceKey);
    }
    if (next.deviceName) {
      localStorage.setItem("edgeDeviceName", next.deviceName);
    }
    if (next.deviceId) {
      localStorage.setItem("edgeDeviceId", next.deviceId);
    }
  };

  const eligibleQrPrintKiosks = useMemo(() => {
    const kiosks = Array.isArray(kiosksData) ? kiosksData : [];
    return kiosks
      .filter((kiosk) => {
        const printingEnabled = kiosk?.printingEnabled === true;
        const bridgeBaseUrl = String(kiosk?.bridgeBaseUrl || "").trim();
        return printingEnabled && bridgeBaseUrl.length > 0;
      })
      .sort((a, b) => {
        const aName = String(a?.deviceName || "").toLowerCase();
        const bName = String(b?.deviceName || "").toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [kiosksData]);

  const headerSubtitle = useMemo(() => {
    const parts = [];
    if (canKds) parts.push("kitchen display");
    if (canOrderDisplay) parts.push("order display");
    if (parts.length === 0) {
      return "Configure general settings for this store.";
    }
    return `Configure ${parts.join(" and ")} behavior.`;
  }, [canKds, canOrderDisplay]);

  const blockedOrderChannels = useMemo(
    () => normalizeBlockedOrderChannels(features?.blockedOrderChannels),
    [features?.blockedOrderChannels],
  );

  const updatePreviewUrl = (setter, ref, nextUrl) => {
    if (ref.current && ref.current.startsWith("blob:")) {
      URL.revokeObjectURL(ref.current);
    }
    ref.current = nextUrl || "";
    setter(nextUrl || "");
  };

  const resolveBannerImageUrl = useCallback(
    (url) => {
      const raw = String(url || "").trim();
      if (!raw) return "";
      if (/^https?:\/\//i.test(raw)) return raw;
      if (raw.startsWith("//")) return `${window.location.protocol}${raw}`;
      return raw.startsWith("/") ? `${baseUrl}${raw}` : `${baseUrl}/${raw}`;
    },
    [baseUrl],
  );

  const fetchBackgroundPreview = useCallback(
    async (url, setter, ref, signal) => {
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: getAuthHeaders(),
          signal,
        });
        if (!response.ok) return;
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        updatePreviewUrl(setter, ref, blobUrl);
      } catch {
        // ignore fetch errors for preview
      }
    },
    [getAuthHeaders],
  );

  const resolveBackgroundUrl = useCallback(
    async (rawUrl, setter, ref, signal) => {
      if (!rawUrl) {
        updatePreviewUrl(setter, ref, "");
        return;
      }

      const url = String(rawUrl);
      if (url.startsWith("blob:") || url.startsWith("data:")) {
        updatePreviewUrl(setter, ref, url);
        return;
      }

      const isApiLike =
        url.startsWith("/") || url.includes("/api/") || url.includes("/edge/");
      const isSameOriginApi = url.startsWith(baseUrl);

      if (isApiLike || isSameOriginApi) {
        const absoluteUrl = url.startsWith("http")
          ? url
          : `${baseUrl}${url.startsWith("/") ? url : `/${url}`}`;
        await fetchBackgroundPreview(absoluteUrl, setter, ref, signal);
        return;
      }

      updatePreviewUrl(setter, ref, url);
    },
    [baseUrl, fetchBackgroundPreview],
  );

  useEffect(() => {
    const controller = new AbortController();
    const shouldShowCustomer = requireCustomerDetails;
    setForm({
      printTokensByStation,
      kitchenDisplayUseStations,
      orderDisplayUseStations,
      showReadyOnly,
      kitchenShowCustomerName: shouldShowCustomer
        ? kitchenShowCustomerName
        : false,
      kitchenShowCustomerPhone: shouldShowCustomer
        ? kitchenShowCustomerPhone
        : false,
      kitchenShowCustomerEmail: shouldShowCustomer
        ? kitchenShowCustomerEmail
        : false,
      orderDisplayShowCustomerName: shouldShowCustomer
        ? orderDisplayShowCustomerName
        : false,
      orderDisplayShowCustomerPhone: shouldShowCustomer
        ? orderDisplayShowCustomerPhone
        : false,
      orderDisplayShowCustomerEmail: shouldShowCustomer
        ? orderDisplayShowCustomerEmail
        : false,
      autoArchiveSecondsInput:
        autoArchiveSeconds === null || autoArchiveSeconds === undefined
          ? ""
          : String(autoArchiveSeconds),
      kdsMaxVisibleOrdersInput:
        kdsMaxVisibleOrders === null || kdsMaxVisibleOrders === undefined
          ? ""
          : String(kdsMaxVisibleOrders),
      posPaymentMode:
        posPaymentMode === "PAY_LATER" ? "PAY_LATER" : "PAY_FIRST",
      tipEnabled: tipConfig?.enabled !== false,
      tipPercentage1Input: String(tipConfig?.percentages?.[0] || ""),
      tipPercentage2Input: String(tipConfig?.percentages?.[1] || ""),
      tipPercentage3Input: String(tipConfig?.percentages?.[2] || ""),
      qrBaseUrl: qrBaseUrl || "",
      qrPrintKioskId: qrPrintKioskId ? String(qrPrintKioskId) : "",
      attractBannersKioskId: fulfillmentSettings?.attractBannersKioskId
        ? String(fulfillmentSettings.attractBannersKioskId)
        : "",
      attractBanners: normalizeAttractBanners(fulfillmentSettings?.attractBanners),
    });
    hasHydratedFormRef.current = true;
    setAutoArchiveError("");
    setKdsMaxVisibleOrdersError("");
    setTipSettingsError("");
    setSaveError("");
    setBannerError("");
    setOrderBackgroundError("");
    setKitchenBackgroundError("");
    resolveBackgroundUrl(
      orderDisplayBackgroundUrl || "",
      setLocalOrderBackgroundUrl,
      orderPreviewRef,
      controller.signal,
    );
    resolveBackgroundUrl(
      kitchenDisplayBackgroundUrl || "",
      setLocalKitchenBackgroundUrl,
      kitchenPreviewRef,
      controller.signal,
    );
    return () => controller.abort();
  }, [
    printTokensByStation,
    kitchenDisplayUseStations,
    orderDisplayUseStations,
    showReadyOnly,
    orderDisplayBackgroundUrl,
    kitchenDisplayBackgroundUrl,
    autoArchiveSeconds,
    kdsMaxVisibleOrders,
    posPaymentMode,
    tipConfig,
    qrBaseUrl,
    qrPrintKioskId,
    fulfillmentSettings,
    kitchenShowCustomerName,
    kitchenShowCustomerPhone,
    kitchenShowCustomerEmail,
    orderDisplayShowCustomerName,
    orderDisplayShowCustomerPhone,
    orderDisplayShowCustomerEmail,
    requireCustomerDetails,
    resolveBackgroundUrl,
  ]);

  useEffect(() => {
    if (channelBlockDirtyRef.current) return;

    setChannelBlockForm({
      kiosk: blockedOrderChannels.includes("KIOSK"),
      qr: blockedOrderChannels.includes("QR"),
      pos: blockedOrderChannels.includes("POS"),
    });
  }, [blockedOrderChannels]);

  useEffect(() => {
    if (requireCustomerDetails) return;
    setForm((prev) => ({
      ...prev,
      kitchenShowCustomerName: false,
      kitchenShowCustomerPhone: false,
      kitchenShowCustomerEmail: false,
      orderDisplayShowCustomerName: false,
      orderDisplayShowCustomerPhone: false,
      orderDisplayShowCustomerEmail: false,
    }));
  }, [requireCustomerDetails]);

  const handleChannelBlockChange = (event) => {
    const { name, checked } = event.target;
    channelBlockDirtyRef.current = true;
    setChannelBlockForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleAllChannelBlocksChange = (event) => {
    const { checked } = event.target;
    channelBlockDirtyRef.current = true;
    setChannelBlockForm({
      kiosk: checked,
      qr: checked,
      pos: checked,
    });
  };

  const allChannelBlocksSelected =
    channelBlockForm.kiosk && channelBlockForm.qr && channelBlockForm.pos;

  useEffect(() => {
    if (requireCustomerDetails) {
      customerAutoSyncRef.current = false;
      return;
    }
    if (!hasHydratedFormRef.current || isLoading) return;
    if (updateState.isLoading || customerAutoSyncRef.current) return;

    const { value: parsedAutoArchiveSeconds, error: autoArchiveErr } =
      parseAutoArchiveSeconds(form.autoArchiveSecondsInput);
    const {
      value: parsedKdsMaxVisibleOrders,
      error: kdsMaxVisibleOrdersValidationErr,
    } = parseKdsMaxVisibleOrders(form.kdsMaxVisibleOrdersInput);

    const payload = {
      printTokensByStation: form.printTokensByStation,
      kitchenDisplayUseStations: form.kitchenDisplayUseStations,
      orderDisplayUseStations: form.orderDisplayUseStations,
      showReadyOnly: form.showReadyOnly,
      kitchenShowCustomerName: false,
      kitchenShowCustomerPhone: false,
      kitchenShowCustomerEmail: false,
      orderDisplayShowCustomerName: false,
      orderDisplayShowCustomerPhone: false,
      orderDisplayShowCustomerEmail: false,
      autoArchiveSeconds:
        autoArchiveErr || parsedAutoArchiveSeconds === null
          ? autoArchiveSeconds ?? null
          : parsedAutoArchiveSeconds,
      kdsMaxVisibleOrders:
        kdsMaxVisibleOrdersValidationErr || parsedKdsMaxVisibleOrders === null
          ? kdsMaxVisibleOrders ?? null
          : parsedKdsMaxVisibleOrders,
      posPaymentMode:
        form.posPaymentMode === "PAY_LATER" ? "PAY_LATER" : "PAY_FIRST",
      tipConfig: {
        enabled: Boolean(form.tipEnabled),
        percentages: normalizeTipPercentagesInput([
          form.tipPercentage1Input,
          form.tipPercentage2Input,
          form.tipPercentage3Input,
        ]),
      },
      attractBannersKioskId: fulfillmentSettings?.attractBannersKioskId
        ? String(fulfillmentSettings.attractBannersKioskId)
        : null,
      attractBanners: normalizeAttractBanners(
        fulfillmentSettings?.attractBanners,
      ).map((banner) => ({
        id: banner.id,
        imageUrl: String(banner.imageUrl || "").trim(),
        sortOrder: Number(banner.sortOrder),
        isActive: banner.isActive !== false,
        startAt: toIsoWithTimezoneOrNull(banner.startAt),
        endAt: toIsoWithTimezoneOrNull(banner.endAt),
        altText: banner.altText ? String(banner.altText).trim() : null,
      })),
    };

    updateFulfillmentSettings(payload)
      .unwrap()
      .then(() => {
        customerAutoSyncRef.current = true;
        refetchSettings();
      })
      .catch(() => {
        // Avoid blocking UI if auto-sync fails; user can retry via Save.
      });
  }, [
    requireCustomerDetails,
    isLoading,
    updateState.isLoading,
    form,
    autoArchiveSeconds,
    kdsMaxVisibleOrders,
    fulfillmentSettings?.attractBanners,
    fulfillmentSettings?.attractBannersKioskId,
    updateFulfillmentSettings,
    refetchSettings,
  ]);

  if (featuresLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="General Settings"
          subtitle="Loading store features..."
        />
        <Card>
          <Alert tone="info">Loading store features...</Alert>
        </Card>
      </div>
    );
  }

  if (featuresError || !features) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="General Settings"
          subtitle="Unable to load store features."
        />
        <Card className="space-y-3">
          <Alert tone="error">Failed to load store features.</Alert>
          <Button size="sm" onClick={() => refetchFeatures && refetchFeatures()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  const applyBackgroundResponse = async (response) => {
    if (!response) return;
    const orderUrl =
      typeof response === "string"
        ? response
        : response.orderDisplayBackgroundUrl || response.orderBackgroundUrl;
    const kitchenUrl =
      typeof response === "string"
        ? response
        : response.kitchenDisplayBackgroundUrl || response.kitchenBackgroundUrl;

    if (orderUrl) {
      await resolveBackgroundUrl(
        orderUrl,
        setLocalOrderBackgroundUrl,
        orderPreviewRef,
      );
    }
    if (kitchenUrl) {
      await resolveBackgroundUrl(
        kitchenUrl,
        setLocalKitchenBackgroundUrl,
        kitchenPreviewRef,
      );
    }
  };

  const handleOrderBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setOrderBackgroundError("");

    const validationError = validateBackgroundFile(file);
    if (validationError) {
      setOrderBackgroundError(validationError);
      return;
    }

    const previousUrl = localOrderBackgroundUrl;
    const tempUrl = URL.createObjectURL(file);
    updatePreviewUrl(setLocalOrderBackgroundUrl, orderPreviewRef, tempUrl);

    try {
      const response = await uploadOrderDisplayBackground(file).unwrap();
      await applyBackgroundResponse(response);
      refetchSettings();
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Order display background updated.",
      });
    } catch (err) {
      updatePreviewUrl(setLocalOrderBackgroundUrl, orderPreviewRef, previousUrl);
      setOrderBackgroundError(
        getApiErrorMessage(err, "Failed to upload order display background."),
      );
    }
  };

  const handleOrderBackgroundRemove = async () => {
    setOrderBackgroundError("");
    try {
      const response = await deleteOrderDisplayBackground().unwrap();
      await applyBackgroundResponse(response);
      refetchSettings();
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Order display background removed.",
      });
    } catch (err) {
      setOrderBackgroundError(
        getApiErrorMessage(err, "Failed to remove order display background."),
      );
    }
  };

  const handleKitchenBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setKitchenBackgroundError("");

    const validationError = validateBackgroundFile(file);
    if (validationError) {
      setKitchenBackgroundError(validationError);
      return;
    }

    const previousUrl = localKitchenBackgroundUrl;
    const tempUrl = URL.createObjectURL(file);
    updatePreviewUrl(setLocalKitchenBackgroundUrl, kitchenPreviewRef, tempUrl);

    try {
      const response = await uploadKitchenDisplayBackground(file).unwrap();
      await applyBackgroundResponse(response);
      refetchSettings();
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Kitchen display background updated.",
      });
    } catch (err) {
      updatePreviewUrl(
        setLocalKitchenBackgroundUrl,
        kitchenPreviewRef,
        previousUrl,
      );
      setKitchenBackgroundError(
        getApiErrorMessage(err, "Failed to upload kitchen display background."),
      );
    }
  };

  const handleKitchenBackgroundRemove = async () => {
    setKitchenBackgroundError("");
    try {
      const response = await deleteKitchenDisplayBackground().unwrap();
      await applyBackgroundResponse(response);
      refetchSettings();
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Kitchen display background removed.",
      });
    } catch (err) {
      setKitchenBackgroundError(
        getApiErrorMessage(err, "Failed to remove kitchen display background."),
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setAutoArchiveError("");
    setKdsMaxVisibleOrdersError("");
    setTipSettingsError("");
    setBannerError("");
    setSaveError("");

    const { value: parsedAutoArchiveSeconds, error: validationError } =
      parseAutoArchiveSeconds(form.autoArchiveSecondsInput);
    const {
      value: parsedKdsMaxVisibleOrders,
      error: kdsValidationError,
    } = parseKdsMaxVisibleOrders(form.kdsMaxVisibleOrdersInput);

    if (validationError) {
      setAutoArchiveError(validationError);
      return;
    }
    if (kdsValidationError) {
      setKdsMaxVisibleOrdersError(kdsValidationError);
      return;
    }
    const tipPercentageInputs = [
      form.tipPercentage1Input,
      form.tipPercentage2Input,
      form.tipPercentage3Input,
    ];
    const parsedTipPercentages = [];
    for (let index = 0; index < tipPercentageInputs.length; index += 1) {
      const { value, error: tipError } = parseTipPercentage(
        tipPercentageInputs[index],
      );
      if (tipError) {
        setTipSettingsError(`Tip ${index + 1}: ${tipError}`);
        return;
      }
      if (value !== null && parsedTipPercentages.includes(value)) {
        setTipSettingsError("Tip percentages must be unique.");
        return;
      }
      if (value !== null) {
        parsedTipPercentages.push(value);
      }
    }
    const attractBannerValidationError = validateAttractBanners(
      form.attractBanners,
    );
    if (attractBannerValidationError) {
      setBannerError(attractBannerValidationError);
      return;
    }

    try {
      const customerVisibilityPayload = requireCustomerDetails
        ? {
            kitchenShowCustomerName: form.kitchenShowCustomerName,
            kitchenShowCustomerPhone: form.kitchenShowCustomerPhone,
            kitchenShowCustomerEmail: form.kitchenShowCustomerEmail,
            orderDisplayShowCustomerName: form.orderDisplayShowCustomerName,
            orderDisplayShowCustomerPhone: form.orderDisplayShowCustomerPhone,
            orderDisplayShowCustomerEmail: form.orderDisplayShowCustomerEmail,
          }
        : {
            kitchenShowCustomerName: false,
            kitchenShowCustomerPhone: false,
            kitchenShowCustomerEmail: false,
            orderDisplayShowCustomerName: false,
            orderDisplayShowCustomerPhone: false,
            orderDisplayShowCustomerEmail: false,
          };

      const blockedOrderChannels = [
        ...normalizeBlockedOrderChannels(features?.blockedOrderChannels).filter(
          (channel) => !["KIOSK", "QR", "POS"].includes(channel),
        ),
        ...(channelBlockForm.kiosk ? ["KIOSK"] : []),
        ...(channelBlockForm.qr ? ["QR"] : []),
        ...(channelBlockForm.pos ? ["POS"] : []),
      ];

      await updateFulfillmentSettings({
        printTokensByStation: form.printTokensByStation,
        kitchenDisplayUseStations: form.kitchenDisplayUseStations,
        orderDisplayUseStations: form.orderDisplayUseStations,
        showReadyOnly: form.showReadyOnly,
        ...customerVisibilityPayload,
        autoArchiveSeconds: parsedAutoArchiveSeconds,
        kdsMaxVisibleOrders: parsedKdsMaxVisibleOrders,
        posPaymentMode:
          form.posPaymentMode === "PAY_LATER" ? "PAY_LATER" : "PAY_FIRST",
        tipConfig: {
          enabled: Boolean(form.tipEnabled),
          percentages: parsedTipPercentages,
        },
        qrBaseUrl: form.qrBaseUrl.trim() || null,
        qrPrintKioskId: form.qrPrintKioskId ? form.qrPrintKioskId : null,
        attractBannersKioskId: form.attractBannersKioskId
          ? form.attractBannersKioskId
          : null,
        attractBanners: form.attractBanners.map((banner) => ({
          id: banner.id,
          imageUrl: String(banner.imageUrl || "").trim(),
          sortOrder: Number(banner.sortOrder),
          isActive: banner.isActive !== false,
          startAt: toIsoWithTimezoneOrNull(banner.startAt),
          endAt: toIsoWithTimezoneOrNull(banner.endAt),
          altText: banner.altText ? String(banner.altText).trim() : null,
        })),
      }).unwrap();

      await updateStoreFeatures({
        storeId: activeStoreId,
        payload: {
          isOrderBlocked: false,
          blockedOrderChannels,
        },
      }).unwrap();

      channelBlockDirtyRef.current = false;
      setChannelBlockForm({
        kiosk: blockedOrderChannels.includes("KIOSK"),
        qr: blockedOrderChannels.includes("QR"),
        pos: blockedOrderChannels.includes("POS"),
      });

      refetchSettings();
      refetchStations();

      setToast({
        id: Date.now(),
        tone: "success",
        message:
          "Settings saved. Display board updates are applied immediately.",
      });
    } catch (err) {
      const code = getApiErrorCode(err);

        if (code === "DISPLAY_AUTO_ARCHIVE_INVALID") {
          setAutoArchiveError(
            err?.data?.detail ||
              err?.data?.message ||
              `Auto archive seconds must be between ${AUTO_ARCHIVE_MIN} and ${AUTO_ARCHIVE_MAX}.`,
          );
          return;
        }
        if (code === "KDS_MAX_VISIBLE_ORDERS_INVALID") {
          setKdsMaxVisibleOrdersError(
            err?.data?.detail ||
              err?.data?.message ||
              `KDS max visible orders must be between ${KDS_MAX_VISIBLE_ORDERS_MIN} and ${KDS_MAX_VISIBLE_ORDERS_MAX}.`,
          );
          return;
        }

      if (code === "DISPLAY_SETTINGS_NOT_FOUND" || err?.status === 404) {
        setSaveError("No display device settings found for this restaurant.");
        return;
      }

      setSaveError(
        err?.data?.detail ||
          err?.data?.message ||
          "Failed to update general settings.",
      );
      setToast({
        id: Date.now(),
        tone: "error",
        message: "Unable to save settings. Please retry.",
      });
    }
  };

  const hasOrderBackground = Boolean(localOrderBackgroundUrl);
  const hasKitchenBackground = Boolean(localKitchenBackgroundUrl);

  const updateBanner = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      attractBanners: prev.attractBanners.map((banner, currentIndex) =>
        currentIndex === index ? { ...banner, ...patch } : banner,
      ),
    }));
  };

  const handleBannerImageUpload = async (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setBannerError("");

    const validationError = await validateAttractBannerFile(file);
    if (validationError) {
      setBannerError(validationError);
      return;
    }

    setBannerUploadingIndex(index);
    try {
      const response = await uploadAttractBannerImage(file).unwrap();
      const imageUrl = String(response?.imageUrl || "").trim();
      if (!imageUrl) {
        throw new Error("Upload succeeded but imageUrl was missing.");
      }
      updateBanner(index, { imageUrl });
    } catch (err) {
      setBannerError(
        getApiErrorMessage(err, "Failed to upload attract banner image."),
      );
    } finally {
      setBannerUploadingIndex(null);
    }
  };

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
        />
      )}

      <PageHeader title="General Settings" subtitle={headerSubtitle} />

      {hasLocalDevices && (
        <Card className="border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Device Selection
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Select which local device this browser session should use.
              </p>
            </div>
            <div className="w-full sm:max-w-xs">
              <Select
                label="Device"
                value={selectedDeviceId}
                onChange={(e) => handleDeviceChange(e.target.value)}
              >
                {localDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {`${device.deviceName || device.deviceKey || device.deviceId} (${device.deviceType || "DEVICE"})`}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </Card>
      )}

      <Card className="border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <form className="space-y-6 pb-2" onSubmit={handleSubmit}>
            {canKds && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div>
                  <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                    Kitchen Display
                  </p>
                  <div className="mt-3">
                    <Checkbox
                      label="Kitchen Display Use Stations"
                      name="kitchenDisplayUseStations"
                      checked={form.kitchenDisplayUseStations}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          kitchenDisplayUseStations: e.target.checked,
                        }))
                      }
                      helper="Use station visibility rules to filter items on the kitchen display."
                    />
                  </div>
                  {requireCustomerDetails && (
                    <div className="mt-4 rounded-md border border-slate-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-900">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Customer Details
                      </p>
                      <div className="mt-3 grid gap-3 md:grid-cols-3">
                        <Checkbox
                          label="Show Customer Name"
                          name="kitchenShowCustomerName"
                          checked={form.kitchenShowCustomerName}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              kitchenShowCustomerName: e.target.checked,
                            }))
                          }
                        />
                        <Checkbox
                          label="Show Customer Phone"
                          name="kitchenShowCustomerPhone"
                          checked={form.kitchenShowCustomerPhone}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              kitchenShowCustomerPhone: e.target.checked,
                            }))
                          }
                        />
                        <Checkbox
                          label="Show Customer Email"
                          name="kitchenShowCustomerEmail"
                          checked={form.kitchenShowCustomerEmail}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              kitchenShowCustomerEmail: e.target.checked,
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                    Kitchen Display Background
                  </p>
                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                    <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
                      {hasKitchenBackground ? (
                        <div className="h-40 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                          <img
                            src={localKitchenBackgroundUrl}
                            alt="Kitchen display background"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="flex h-40 w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400">
                          No background uploaded.
                        </div>
                      )}
                      <div className="space-y-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Upload a PNG/JPG background for kitchen display screens.
                        </p>
                        <input
                          ref={kitchenFileRef}
                          className="sr-only"
                          name="kitchenDisplayBackground"
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={handleKitchenBackgroundUpload}
                          disabled={
                            uploadKitchenState.isLoading ||
                            deleteKitchenState.isLoading
                          }
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => kitchenFileRef.current?.click()}
                            disabled={
                              uploadKitchenState.isLoading ||
                              deleteKitchenState.isLoading
                            }
                          >
                            {hasKitchenBackground
                              ? "Replace Background"
                              : "Choose File"}
                          </Button>
                          {hasKitchenBackground && (
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={handleKitchenBackgroundRemove}
                              disabled={
                                uploadKitchenState.isLoading ||
                                deleteKitchenState.isLoading
                              }
                            >
                              {deleteKitchenState.isLoading
                                ? "Removing..."
                                : "Remove Background"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {kitchenBackgroundError && (
                    <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                      {kitchenBackgroundError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {canOrderDisplay && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div>
                  <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                    Order Display
                  </p>
                  <div className="mt-3 space-y-4">
                    <Checkbox
                      label="Order Display Use Stations"
                      name="orderDisplayUseStations"
                      checked={form.orderDisplayUseStations}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          orderDisplayUseStations: e.target.checked,
                        }))
                      }
                      helper="Use station visibility rules to filter items on the order display."
                    />
                    <Checkbox
                      label="Show Ready Orders Only"
                      name="showReadyOnly"
                      checked={form.showReadyOnly}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          showReadyOnly: e.target.checked,
                        }))
                      }
                    />

                    <Input
                      label="Auto Archive Ready Orders (seconds)"
                      name="autoArchiveSeconds"
                      type="number"
                      min={AUTO_ARCHIVE_MIN}
                      max={AUTO_ARCHIVE_MAX}
                      value={form.autoArchiveSecondsInput}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          autoArchiveSecondsInput: e.target.value,
                        }))
                      }
                      helper="Ready orders older than this duration are hidden from display board."
                      error={autoArchiveError}
                      placeholder="Leave empty to disable auto-archive"
                    />
                  </div>
                  {requireCustomerDetails && (
                    <div className="mt-4 rounded-md border border-slate-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-900">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Customer Details
                      </p>
                      <div className="mt-3 grid gap-3 md:grid-cols-3">
                        <Checkbox
                          label="Show Customer Name"
                          name="orderDisplayShowCustomerName"
                          checked={form.orderDisplayShowCustomerName}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              orderDisplayShowCustomerName: e.target.checked,
                            }))
                          }
                        />
                        <Checkbox
                          label="Show Customer Phone"
                          name="orderDisplayShowCustomerPhone"
                          checked={form.orderDisplayShowCustomerPhone}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              orderDisplayShowCustomerPhone: e.target.checked,
                            }))
                          }
                        />
                        <Checkbox
                          label="Show Customer Email"
                          name="orderDisplayShowCustomerEmail"
                          checked={form.orderDisplayShowCustomerEmail}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              orderDisplayShowCustomerEmail: e.target.checked,
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                    Order Display Background
                  </p>
                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                    <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
                      {hasOrderBackground ? (
                        <div className="h-40 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                          <img
                            src={localOrderBackgroundUrl}
                            alt="Order display background"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="flex h-40 w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400">
                          No background uploaded.
                        </div>
                      )}
                      <div className="space-y-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Upload a PNG/JPG background for order display screens.
                        </p>
                        <input
                          ref={orderFileRef}
                          className="sr-only"
                          name="orderDisplayBackground"
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={handleOrderBackgroundUpload}
                          disabled={
                            uploadOrderState.isLoading ||
                            deleteOrderState.isLoading
                          }
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => orderFileRef.current?.click()}
                            disabled={
                              uploadOrderState.isLoading ||
                              deleteOrderState.isLoading
                            }
                          >
                            {hasOrderBackground
                              ? "Replace Background"
                              : "Choose File"}
                          </Button>
                          {hasOrderBackground && (
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={handleOrderBackgroundRemove}
                              disabled={
                                uploadOrderState.isLoading ||
                                deleteOrderState.isLoading
                              }
                            >
                              {deleteOrderState.isLoading
                                ? "Removing..."
                                : "Remove Background"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {orderBackgroundError && (
                    <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                      {orderBackgroundError}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid gap-5 xl:grid-cols-2">
            {showStationFeatures && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                  Printing / Fulfillment
                </p>
                <div className="mt-3">
                  <Checkbox
                    label="Print Tokens By Station"
                    name="printTokensByStation"
                    checked={form.printTokensByStation}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        printTokensByStation: e.target.checked,
                      }))
                    }
                    helper="Print separate kitchen tokens by station."
                  />
                </div>
                <div className="mt-4">
                  <Input
                    label="KDS Max Visible Orders"
                    name="kdsMaxVisibleOrders"
                    type="number"
                    min={KDS_MAX_VISIBLE_ORDERS_MIN}
                    max={KDS_MAX_VISIBLE_ORDERS_MAX}
                    step={1}
                    value={form.kdsMaxVisibleOrdersInput}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        kdsMaxVisibleOrdersInput: e.target.value,
                      }))
                    }
                    helper="Controls how many KDS cards are visible at once. Remaining cards stay in hidden queue."
                    error={kdsMaxVisibleOrdersError}
                    placeholder="Default is 10 when not configured"
                  />
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                POS Fulfillment
              </p>
              <div className="mt-3">
                <Select
                  label="POS Payment Mode"
                  name="posPaymentMode"
                  value={form.posPaymentMode}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      posPaymentMode:
                        e.target.value === "PAY_LATER"
                          ? "PAY_LATER"
                          : "PAY_FIRST",
                    }))
                  }
                  helper="Pay First: payment required before kitchen submit. Pay Later: submit to kitchen before final payment."
                >
                  <option value="PAY_FIRST">Pay First</option>
                  <option value="PAY_LATER">Pay Later</option>
                </Select>
              </div>
            </div>

            {SHOW_TIP_UI && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                  Tip Settings
                </p>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  Configure whether the POS shows tips and up to three preset percentages.
                </p>
                <div className="mt-4 space-y-4">
                  <Checkbox
                    label="Enable Tips"
                    name="tipEnabled"
                    checked={form.tipEnabled}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        tipEnabled: e.target.checked,
                      }))
                    }
                    helper="When enabled, preset tips and custom tip entry are available."
                  />
                  <div className="grid gap-3 md:grid-cols-3">
                    {[1, 2, 3].map((index) => {
                      const inputKey = `tipPercentage${index}Input`;

                      return (
                        <Input
                          key={index}
                          label={`Tip Percentage ${index}`}
                          name={`tipPercentage${index}`}
                          type="number"
                          min={1}
                          step={1}
                          value={form[inputKey]}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              [inputKey]: e.target.value,
                            }))
                          }
                          disabled={!form.tipEnabled}
                          placeholder="Enter percentage"
                        />
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Leave unused percentage fields blank. The POS will only show the filled values when tips are enabled.
                  </p>
                </div>
                {tipSettingsError && (
                  <div className="mt-3 text-xs text-rose-600 dark:text-rose-400">
                    {tipSettingsError}
                  </div>
                )}
              </div>
            )}

            {canQr && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 xl:col-span-2">
                <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                  QR Code Configuration
                </p>
                <div className="mt-3">
                  <Input
                    label="QR Base URL"
                    name="qrBaseUrl"
                    type="url"
                    value={form.qrBaseUrl}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        qrBaseUrl: e.target.value,
                      }))
                    }
                    helper="Base URL used for generating QR codes for table ordering or order tracking."
                    placeholder="https://example.com"
                  />
                </div>
                {enableKiosk && <div className="mt-4">
                  <Select
                    label="QR Print Device"
                    name="qrPrintKioskId"
                    value={form.qrPrintKioskId}
                    disabled={kiosksLoading}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        qrPrintKioskId: e.target.value,
                      }))
                    }
                    helper={
                      eligibleQrPrintKiosks.length === 0
                        ? "No printing kiosks configured. Enable printing and set Bridge base URL on a kiosk first."
                        : form.qrPrintKioskId
                          ? "Bridge-enabled kiosk used to print QR receipts and kitchen tickets."
                          : "None selected. QR receipts won’t print until this is configured."
                    }
                  >
                    <option value="">None</option>
                    {eligibleQrPrintKiosks.map((kiosk) => (
                      <option key={kiosk.kioskId} value={kiosk.kioskId}>
                        {kiosk.deviceName || kiosk.deviceKey || kiosk.kioskId}
                      </option>
                    ))}
                  </Select>
                  {kiosksError && (
                    <div className="mt-2">
                      <Alert tone="error">
                        {kiosksErrorDetail?.data?.detail ||
                          kiosksErrorDetail?.data?.message ||
                          "Failed to load kiosks."}
                      </Alert>
                    </div>
                  )}
                </div>}
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 xl:col-span-2">
              <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                Ordering Access
              </p>
              <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                Block Kiosk, QR, or POS ordering from this store.
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-6">
                <Checkbox
                  label="Select All"
                  name="all"
                  checked={allChannelBlocksSelected}
                  onChange={handleAllChannelBlocksChange}
                />
                <Checkbox
                  label="Kiosk"
                  name="kiosk"
                  checked={channelBlockForm.kiosk}
                  onChange={handleChannelBlockChange}
                />
                <Checkbox
                  label="QR"
                  name="qr"
                  checked={channelBlockForm.qr}
                  onChange={handleChannelBlockChange}
                />
                <Checkbox
                  label="POS"
                  name="pos"
                  checked={channelBlockForm.pos}
                  onChange={handleChannelBlockChange}
                />
              </div>
              <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Select any channel to stop new orders from it.
              </div>
            </div>

            {enableKiosk && <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 xl:col-span-2">
              <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                Kiosk Attract Banners
              </p>
              <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                Configure full-screen attract banners shown on kiosk welcome screen.
              </p>
              <div className="mt-4">
                <Input
                  label="Attract Banners Kiosk ID (optional)"
                  name="attractBannersKioskId"
                  value={form.attractBannersKioskId}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      attractBannersKioskId: e.target.value,
                    }))
                  }
                  helper="Kept for payload compatibility. Leave empty to use store/display level banners."
                />
              </div>
              <div className="mt-4 space-y-3">
                {form.attractBanners.map((banner, index) => (
                  (() => {
                    const windowStatus = getBannerWindowStatus(banner);
                    return (
                  <div
                    key={banner.id || `banner-${index}`}
                    className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900"
                  >
                    <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
                      <div>
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Banner Image #{index + 1}
                          </p>
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getBannerStatusToneClass(
                              windowStatus,
                            )}`}
                          >
                            {windowStatus}
                          </span>
                        </div>
                        {banner.imageUrl ? (
                          <div className="h-[380px] w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                            <img
                              src={resolveBannerImageUrl(banner.imageUrl)}
                              alt={banner.altText || `Banner ${index + 1}`}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="flex h-[380px] w-full items-center justify-center rounded-md border border-dashed border-slate-300 bg-white text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400">
                            No image uploaded
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div>
                          <input
                            id={`banner-upload-${banner.id || index}`}
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={(e) => handleBannerImageUpload(index, e)}
                            disabled={uploadAttractBannerState.isLoading}
                            className="hidden"
                          />
                          <label
                            htmlFor={`banner-upload-${banner.id || index}`}
                            className={`inline-flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm font-medium transition ${
                              uploadAttractBannerState.isLoading
                                ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                            }`}
                          >
                            {banner.imageUrl ? "Replace Image" : "Choose Image"}
                          </label>
                          {bannerUploadingIndex === index && (
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              Uploading...
                            </p>
                          )}
                        </div>
                        <Input
                          label="Alt Text (optional)"
                          value={banner.altText}
                          onChange={(e) =>
                            updateBanner(index, { altText: e.target.value })
                          }
                          placeholder="Summer Offer"
                        />
                        <Input
                          label="Sort Order"
                          type="number"
                          step={1}
                          value={banner.sortOrder}
                          onChange={(e) =>
                            updateBanner(index, {
                              sortOrder: Number(e.target.value),
                            })
                          }
                        />
                        <div className="grid gap-3 md:grid-cols-2">
                          <Input
                            label="Start At (optional)"
                            type="datetime-local"
                            value={banner.startAt || ""}
                            onChange={(e) =>
                              updateBanner(index, { startAt: e.target.value })
                            }
                          />
                          <Input
                            label="End At (optional)"
                            type="datetime-local"
                            value={banner.endAt || ""}
                            onChange={(e) =>
                              updateBanner(index, { endAt: e.target.value })
                            }
                          />
                        </div>
                        <div className="pt-1">
                          <Checkbox
                            label="Active"
                            checked={banner.isActive !== false}
                            onChange={(e) =>
                              updateBanner(index, { isActive: e.target.checked })
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            attractBanners: prev.attractBanners.filter(
                              (_, currentIndex) => currentIndex !== index,
                            ),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                    );
                  })()
                ))}
              </div>
              <div className="mt-3">
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      attractBanners: [...prev.attractBanners, createBannerDraft()],
                    }))
                  }
                >
                  Add Banner
                </Button>
              </div>
              {bannerError && (
                <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                  {bannerError}
                </div>
              )}
            </div>}
            </div>

            {(canKds || canOrderDisplay) && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-700 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300">
                Display board updates are applied immediately.
              </div>
            )}

            {(canKds || canOrderDisplay) && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm dark:border-slate-700 dark:bg-slate-800">
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  Station Feature Status
                </p>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Active stations: {activeStations.length}
                </p>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Station fields in category/item forms will be visible when at least
                  one station-based feature is enabled and active stations exist.
                </p>
              </div>
            )}

            {isLoading && (
              <Alert tone="info">Loading general settings...</Alert>
            )}
            {isError && (
              <Alert tone="error">{getSettingsErrorMessage(error)}</Alert>
            )}
            <div className="sticky bottom-0 z-30 flex items-center justify-end gap-3 border-t border-slate-200 bg-white/95 px-2 py-3 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
              <Button type="submit" disabled={updateState.isLoading}>
                {updateState.isLoading ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </form>
        </Card>

      <Modal
        isOpen={Boolean(saveError)}
        onClose={() => setSaveError("")}
        title="Unable to Save Settings"
        size="md"
      >
        <p className="text-sm text-slate-700 dark:text-slate-300">{saveError}</p>
      </Modal>
    </div>
  );
};

export default FulfillmentSettings;
